import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import Dialog from "@material-ui/core/Dialog";
import Button from "@material-ui/core/Button";
import Editor from "@monaco-editor/react";
import { useAppSettings } from "Contexts/AppSettingsProvider";

const JSON_EDITOR_OPTIONS = {
  automaticLayout: true,
  minimap: { enabled: false },
  scrollBeyondLastLine: false,
  folding: true,
  foldingStrategy: "indentation",
  showFoldingControls: "always",
  formatOnPaste: true,
  tabSize: 2,
  fontSize: 13,
  lineNumbers: "on",
  wordWrap: "off",
  renderLineHighlight: "line",
  matchBrackets: "never",
  bracketPairColorization: { enabled: false },
  guides: { bracketPairs: false, indentation: false },
};

const JSON_EDITOR_MIN_HEIGHT = 360;

const isEmptyJsonValue = (value) => {
  if (value == null) return true;
  if (typeof value === "object") {
    if (Array.isArray(value)) return value.length === 0;
    return Object.keys(value).length === 0;
  }
  const text = String(value).trim();
  if (text === "") return true;
  try {
    const parsed = JSON.parse(text);
    if (parsed == null) return true;
    if (typeof parsed === "object" && !Array.isArray(parsed)) {
      return Object.keys(parsed).length === 0;
    }
    if (typeof parsed === "string") {
      const inner = parsed.trim();
      if (inner === "" || inner === "{}") return true;
      try {
        const innerParsed = JSON.parse(inner);
        return (
          innerParsed &&
          typeof innerParsed === "object" &&
          !Array.isArray(innerParsed) &&
          Object.keys(innerParsed).length === 0
        );
      } catch {
        return false;
      }
    }
    return false;
  } catch {
    return text === "{}";
  }
};

const formatJsonDraft = (value) => {
  if (isEmptyJsonValue(value)) return "";
  if (typeof value === "object") {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return "";
    }
  }
  const text = String(value);
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
};

const JsonEditorModal = ({
  open,
  title = "JSON Editor",
  value = "",
  onClose,
  onSave,
}) => {
  const { appTheme } = useAppSettings();
  const [draft, setDraft] = useState("");
  const [error, setError] = useState(null);
  const [editorHeight, setEditorHeight] = useState(JSON_EDITOR_MIN_HEIGHT);
  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const bodyRef = useRef(null);

  const editorTheme = appTheme === "Dark" ? "vs-dark" : "vs";

  useEffect(() => {
    if (!open) return;
    setDraft(formatJsonDraft(value));
    setError(null);
  }, [open, value]);

  const layoutEditor = useCallback(() => {
    if (bodyRef.current) {
      const nextHeight = bodyRef.current.clientHeight;
      if (nextHeight > 0) {
        setEditorHeight(nextHeight);
      }
    }
    editorRef.current?.layout();
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;

    layoutEditor();
    const frameId = requestAnimationFrame(layoutEditor);
    const timeoutId = window.setTimeout(layoutEditor, 150);
    window.addEventListener("resize", layoutEditor);

    return () => {
      cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
      window.removeEventListener("resize", layoutEditor);
    };
  }, [open, layoutEditor]);

  useEffect(() => {
    if (!monacoRef.current) return;
    monacoRef.current.editor.setTheme(editorTheme);
    layoutEditor();
  }, [editorTheme, layoutEditor]);

  const handleEditorMount = useCallback((editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;
    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
      validate: true,
      allowComments: false,
    });
    layoutEditor();
  }, [layoutEditor]);

  const handleFormat = useCallback(async () => {
    const trimmed = draft.trim();
    if (!trimmed) {
      setDraft("");
      setError(null);
      return;
    }
    try {
      JSON.parse(trimmed);
      const editor = editorRef.current;
      if (editor) {
        await editor.getAction("editor.action.formatDocument")?.run();
        setDraft(editor.getValue());
      } else {
        setDraft(JSON.stringify(JSON.parse(trimmed), null, 2));
      }
      setError(null);
    } catch {
      setError("Invalid JSON format");
    }
  }, [draft]);

  const handleSave = useCallback(() => {
    const trimmed = draft.trim();
    if (!trimmed) {
      onSave("{}");
      onClose();
      return;
    }
    try {
      const parsed = JSON.parse(trimmed);
      onSave(JSON.stringify(parsed));
      onClose();
    } catch {
      setError("Invalid JSON format");
    }
  }, [draft, onClose, onSave]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      className="jsonEditorModal"
      TransitionProps={{ onEntered: layoutEditor }}
    >
      <div className="modalTitleHolder mb16">
        <h1 className="modalTitle">{title}</h1>
      </div>
      <div className="jsonEditorModalEditor">
        <div className="jsonEditorModalEditorToolbar">
          <button
            type="button"
            className="jsonEditorFormatBtn"
            onClick={handleFormat}
          >
            Format
          </button>
        </div>
        <div ref={bodyRef} className="jsonEditorModalBody">
          {open && (
            <Editor
              height={editorHeight}
              language="json"
              theme={editorTheme}
              value={draft}
              onChange={(nextValue) => {
                setDraft(nextValue ?? "");
                if (error) setError(null);
              }}
              onMount={handleEditorMount}
              options={JSON_EDITOR_OPTIONS}
            />
          )}
        </div>
      </div>
      {error && <p className="jsonEditorModalError">{error}</p>}
      <div className="btnHolder end mt10 fixedWidthBtn">
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save
        </Button>
      </div>
    </Dialog>
  );
};

export default JsonEditorModal;
